import type { ReactNode } from "react"

import { Badge, type BadgeColor, StatusBadge } from "@/components/ui/badge"

export interface BubbleSourceLine {
  id: string
  kind: "inbound" | "outbound"
  step_code: string | null
  body: string
  sent_at: string
}

export interface DecoratedBubble extends BubbleSourceLine {
  text: ReactNode
  reply_to_step?: string | null
}

const RATING_STEPS = new Set(["Q1", "Q2", "Q3"])
const RATING_LABEL: Record<string, string> = {
  Q1: "Q1 rating",
  Q2: "Q2 rating",
  Q3: "Q3 rating",
}

interface DecorateOptions {
  closedListeningAt: string | null
}

/**
 * Walks the transcript chronologically; for each inbound, infers the step
 * being replied to (most recent outbound's step_code) and attaches a
 * decorated `text` ReactNode that wraps the message body with a contextual
 * Badge — rating digits, START/STOP keywords, and late replies.
 */
export function decorateBubbles(
  lines: BubbleSourceLine[],
  { closedListeningAt }: DecorateOptions,
): DecoratedBubble[] {
  let lastOutboundStep: string | null = null
  const closedAt = closedListeningAt
    ? new Date(closedListeningAt).getTime()
    : null

  return lines.map((line) => {
    if (line.kind === "outbound") {
      lastOutboundStep = line.step_code
      return { ...line, text: line.body }
    }

    const replyTo = lastOutboundStep
    const trimmed = line.body.trim()
    const sentAtMs = new Date(line.sent_at).getTime()
    const isLate = closedAt !== null && sentAtMs > closedAt

    // Rating reply (single digit 1–5 to a Q1/Q2/Q3 prompt)
    if (replyTo && RATING_STEPS.has(replyTo) && /^[1-5]$/.test(trimmed)) {
      return {
        ...line,
        reply_to_step: replyTo,
        text: bubbleWithBadge({
          body: <span className="text-xl font-semibold">{trimmed}</span>,
          badgeLabel: RATING_LABEL[replyTo] ?? `${replyTo} rating`,
          badgeColor: "indigo",
          lateBadge: isLate,
        }),
      }
    }

    // Opt-in
    if (trimmed.toUpperCase() === "START") {
      return {
        ...line,
        reply_to_step: replyTo,
        text: bubbleWithBadge({
          body: trimmed,
          badgeLabel: "Opt-in",
          badgeColor: "success",
          lateBadge: isLate,
        }),
      }
    }

    // Opt-out
    if (trimmed.toUpperCase() === "STOP") {
      return {
        ...line,
        reply_to_step: replyTo,
        text: bubbleWithBadge({
          body: trimmed,
          badgeLabel: "Opt-out",
          badgeColor: "error",
          lateBadge: isLate,
        }),
      }
    }

    // Plain text — still flag late replies
    if (isLate) {
      return {
        ...line,
        reply_to_step: replyTo,
        text: bubbleWithBadge({
          body: line.body,
          lateBadge: true,
        }),
      }
    }

    return { ...line, reply_to_step: replyTo, text: line.body }
  })
}

function bubbleWithBadge({
  body,
  badgeLabel,
  badgeColor,
  lateBadge,
}: {
  body: ReactNode
  badgeLabel?: string
  badgeColor?: BadgeColor<"color">
  lateBadge?: boolean
}) {
  return (
    <span className="flex flex-wrap items-center gap-2">
      <span>{body}</span>
      {badgeLabel && badgeColor && (
        <Badge color={badgeColor} size="sm">
          {badgeLabel}
        </Badge>
      )}
      {lateBadge && <StatusBadge tier="caution">Late reply</StatusBadge>}
    </span>
  )
}
